import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { LOGO } from "@/lib/brand";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Lock, Loader2, ArrowLeft } from "lucide-react";

export default function AdminLogin() {
  const { adminLogin } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState(
    new URLSearchParams(window.location.search).get("expired") ? "Session expired — Please sign in again" : ""
  );
  const [busy, setBusy] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await adminLogin(email, password);
      navigate("/admin");
    } catch (err) {
      const d = err.response?.data?.detail;
      setError(typeof d === "string" ? d : "Invalid email or password");
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col text-[#F4FFF9]"
      style={{ background: "radial-gradient(130% 90% at 50% 0%, #0D3B2D 0%, #071A13 45%, #030B08 100%)" }}>
      <div className="p-6">
        <button onClick={() => navigate("/")} className="flex items-center gap-2.5" data-testid="adminlogin-home">
          <img src={LOGO.icon} alt="ECOREXS" className="h-9 w-auto object-contain" />
          <img src={LOGO.wordmark} alt="ECOREXS" className="h-6 w-auto object-contain" />
        </button>
      </div>
      <div className="flex-1 flex items-center justify-center px-6 pb-20">
        <form onSubmit={submit} className="w-full max-w-md bg-white/[0.04] border border-[#67F2B3]/20 backdrop-blur-xl rounded-2xl p-8 animate-fade-up" data-testid="admin-login-form">
          <div className="inline-flex items-center justify-center h-12 w-12 rounded-xl bg-[#67F2B3]/12 border border-[#67F2B3]/25 mb-5">
            <Lock className="h-6 w-6 text-[#67F2B3]" />
          </div>
          <h1 className="font-heading font-bold text-2xl">Administrator Access</h1>
          <p className="text-sm text-[#F4FFF9]/60 mt-2 mb-6">Private area. Authorized personnel only.</p>

          {error && (
            <div className="mb-4 text-sm text-red-300 bg-red-500/10 border border-red-500/30 rounded-lg px-3 py-2" data-testid="admin-login-error">
              {error}
            </div>
          )}
          <div className="space-y-4">
            <div>
              <Label className="text-[#F4FFF9]/80">Email</Label>
              <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required
                data-testid="admin-email-input"
                className="bg-white/5 border-[#67F2B3]/20 text-white placeholder:text-white/30 focus:border-[#67F2B3]" placeholder="you@example.com" />
            </div>
            <div>
              <Label className="text-[#F4FFF9]/80">Password</Label>
              <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required
                data-testid="admin-password-input"
                className="bg-white/5 border-[#67F2B3]/20 text-white placeholder:text-white/30 focus:border-[#67F2B3]" placeholder="••••••••" />
            </div>
          </div>
          <Button type="submit" disabled={busy} data-testid="admin-login-submit"
            className="w-full mt-6 bg-[#67F2B3] hover:bg-[#8affca] text-[#071A13] font-semibold py-6 rounded-xl">
            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in to CRM"}
          </Button>
          <p className="text-xs text-[#F4FFF9]/40 mt-5 text-center">
            Client access? <button type="button" onClick={() => navigate("/login")} className="text-[#67F2B3]/80 hover:text-[#67F2B3]" data-testid="go-client-login">Client Login</button>
          </p>
          <button type="button" onClick={() => navigate("/")} data-testid="admin-back-home"
            className="w-full mt-3 flex items-center justify-center gap-1.5 text-xs text-[#F4FFF9]/50 hover:text-[#67F2B3] transition-colors">
            <ArrowLeft className="h-3.5 w-3.5" /> Back to Home
          </button>
        </form>
      </div>
    </div>
  );
}
